<template>
  <div
    class="
      tw-mb-4 tw-w-full tw-flex tw-flex-row tw-justify-between tw-items-center
    "
  >
    <span>
      <span v-if="anySelected" class="tw-ps-6 tw-pe-4">
        <SfCheckbox
          display="block"
          :model-value="anySelected"
          :indeterminate="anySelected && !allSelected"
          @update:model-value="handleGlobalToggle"
        />
      </span>
      <span class="sf-font-l sf-title">{{ ledger }}</span>
    </span>
    <div v-if="anySelected" class="tw-flex tw-flex-row tw-gap-4">
      {{
        I18n.t('js.export.count_selected', { count: selectedExportIds.length })
      }}
      <span>
        <ExportItemAction
          v-if="selectedAllSucceeded"
          data-testid="export-ledger-section-bulk-move"
          icon="folder-open"
          :tooltip="I18n.t('js.export.copy_to_documents')"
          @click="handleBulkMove"
        />
        <ExportItemAction
          icon="trash"
          data-testid="export-ledger-section-bulk-delete"
          :tooltip="I18n.t('confirmation.delete')"
          @click="handleBulkDelete"
        />
      </span>
    </div>
  </div>
  <div class="sf-tab sf-layer-white sf-below-3">
    <div v-for="exportItem in exportsWithSelected" :key="exportItem.export.key">
      <ExportItem
        :export-item="exportItem.export"
        :app-links="appLinks"
        :dropdown-expanded="openedDropdownExportId === exportItem.export.id"
        :selected="exportItem.selected"
        :any-selected="anySelected"
        @toggle-dropdown="handleToggleDropdown"
        @close-dropdown="handleCloseDropdown"
        @toggle-selected="handleToggleSelected"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ExportItem from '@app/application/export/screens/exports/components/export-item.vue'
import type { AppLink, ExportType } from '@app/application/export/types'
import { computed, ref } from 'vue'
import { SfCheckbox } from '@silverfin/ui-components'
import ExportItemAction from '@app/application/export/screens/exports/components/export-item-action.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import api from '@app/application/export/screens/exports/api/export-item'
import { SfLog } from '@app/adapters/logger'

const I18n = useI18n()

interface Props {
  ledger: string
  exports: ExportType[]
  appLinks: AppLink[]
  openedDropdownExportId: number | null
}

const props = defineProps<Props>()

const emit = defineEmits<{ toggleDropdown: [number]; closeDropdown: [] }>()

const selectedExportIds = ref<number[]>([])

const exportsWithSelected = computed(() =>
  props.exports.map((e) => ({
    export: e,
    selected: selectedExportIds.value.includes(e.id),
  }))
)

const anySelected = computed(() => selectedExportIds.value.length > 0)
const allSelected = computed(
  () => selectedExportIds.value.length === props.exports.length
)
const selectedAllSucceeded = computed(() =>
  exportsWithSelected.value.every(
    (e) => !e.selected || e.export.status === 'succeeded'
  )
)

function handleGlobalToggle() {
  if (allSelected.value) {
    selectedExportIds.value = []
  } else {
    selectedExportIds.value = props.exports.map((e) => e.id)
  }
}

function handleToggleSelected(id: number) {
  const index = selectedExportIds.value.indexOf(id)
  if (index >= 0) {
    selectedExportIds.value.splice(index, 1)
  } else {
    selectedExportIds.value.push(id)
  }
}

function handleToggleDropdown(id: number) {
  emit('toggleDropdown', id)
}

function handleCloseDropdown() {
  emit('closeDropdown')
}

async function handleBulkMove() {
  try {
    await api.moveExports(selectedExportIds.value)
  } catch (e) {
    SfLog.error('Failed to fetch modal for moving exports', { error: e })
  }
}

function handleBulkDelete() {
  window.sfRemoveConfirm({
    name: I18n.t('js.export.count_selected_exports', {
      count: selectedExportIds.value.length,
    }),
    onConfirm: async () => {
      try {
        await api.deleteExports(selectedExportIds.value)
      } catch (e) {
        SfLog.error('Failed to delete exports', { error: e })
      }
    },
    keep: false,
  })
}
</script>
